package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.DisplayStyle
import dev.lingvo.shared.DesignAssetKind
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.localeInfo
import dev.lingvo.shared.screenshots.doc.BrandKit
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.XhrUpload
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.api.putScreenshotDesign
import dev.lingvo.site.api.putScreenshotDesignAsset
import dev.lingvo.site.api.screenshotDesign
import dev.lingvo.site.api.screenshotPushJobs
import dev.lingvo.shared.ShotPushState
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.browser.document
import dev.lingvo.site.components.studio.editor.CanvasDesignWorkspace
import dev.lingvo.site.components.studio.editor.DesignMode
import dev.lingvo.site.components.studio.preview.DesignLooks
import dev.lingvo.site.components.studio.preview.DesignLooksPort
import dev.lingvo.site.components.studio.preview.projectIconPixels
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LColorField
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSegmented
import dev.lingvo.site.ds.LSegmentedOption
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.nav.UrlState
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.browser.window
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.Event
import kotlin.js.Date

/** Hides layout controls at narrow widths even when a preview is embedded in another viewport. */
val DesignEditingControlsStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD { Modifier.display(DisplayStyle.Flex) }
}

/** Keeps the narrow-screen explanation aligned with the same breakpoint as its controls. */
val DesignNarrowNoticeStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Block) }
    Breakpoint.MD { Modifier.display(DisplayStyle.None) }
}

/** Separates controls from review at desktop widths while allowing the review list to shrink. */
val DesignWorkspaceBodyStyle = CssStyle {
    base { Modifier.styleModifier { property("display", "grid"); property("grid-template-columns", "minmax(0, 1fr)"); property("gap", Space.LG.toString()); property("min-width", "0") } }
    Breakpoint.MD { Modifier.styleModifier { property("grid-template-columns", "minmax(240px, 320px) minmax(0, 1fr)") } }
}

/** One authority-bound resource host serves both URLs without carrying state between projects. */
@Composable
internal fun DesignWorkspace(templates: Boolean = false) {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    val route = liveProjectRoute(slug)
    val subject = route?.authoritySubject(Session.organizations)
    var params by remember(slug) { mutableStateOf(readScreenshotsAreaState(ctx)) }
    var mode by remember(slug) { mutableStateOf(DesignMode.parse(UrlState.read(ctx, "mode"))) }
    var captionOpen by remember(subject) { mutableStateOf(false) }
    var blockedNavigation by remember { mutableStateOf(false) }
    fun acceptUrl(next: ScreenshotsAreaState, selected: DesignMode) {
        if (captionOpen && next.platform != params.platform) {
            blockedNavigation = true
            UrlState.replace(params.toParams(ScreenshotsTab.DESIGN) + ("mode" to mode.id.takeIf { mode == DesignMode.CANVAS }))
        } else { params = next; mode = selected }
    }
    LaunchedEffect(slug, ctx.route.queryParams) {
        acceptUrl(readScreenshotsAreaState(ctx), DesignMode.parse(UrlState.read(ctx, "mode")))
    }
    DisposableEffect(slug) {
        val pop: (Event) -> Unit = {
            val query = js("new URLSearchParams(window.location.search)")
            acceptUrl(ScreenshotsAreaState.parse(listOf("platform", "device", "slot", "locale", "layer", "category")
                .associateWith { query.get(it) as? String }), DesignMode.parse(query.get("mode") as? String))
        }
        window.addEventListener("popstate", pop)
        onDispose { window.removeEventListener("popstate", pop) }
    }
    var reload by remember(subject, params.platform) { mutableStateOf(0) }
    val state = loadReloadable(subject, params.platform, reloadToken = reload) {
        val current = subject ?: awaitCancellation()
        requestProjectResource(current.action, slug) { verified -> loadDesignPage(verified.project, params.platform) }
    }
    val authority = ProjectAuthority.resolve(route?.project, Session.organizations)
    // The padding keeps focus rings from being clipped by the scroll container; the negative margin
    // hands it back, so this page's heading sits on the same x as Plan's and Review's instead of
    // jumping 12px every time the tab changes.
    Column(Modifier.fillMaxWidth().gap(Space.LG).styleModifier {
        property("min-width", "0"); property("min-height", "0"); property("max-height", "100%"); property("overflow-y", "auto"); property("box-sizing", "border-box")
        property("padding", Space.MD.toString()); property("margin", "-${Space.MD}"); property("width", "calc(100% + ${Space.MD} + ${Space.MD})"); property("max-width", "calc(100% + ${Space.MD} + ${Space.MD})")
    }) {
        PageHeader(title = if (templates) t("app.projects.slug.screenshots.design_screen.templates", "Templates")
            else t("app.projects.slug.screenshots.design_screen.design", "Screenshot design"))
        ScreenshotsAreaNav(slug, ScreenshotsTab.DESIGN, params, onPlatformChange = {
            if (captionOpen) {
                blockedNavigation = true
                UrlState.replace(params.toParams(ScreenshotsTab.DESIGN) + ("mode" to mode.id.takeIf { mode == DesignMode.CANVAS }))
            } else params = it
        })
        if (captionOpen && blockedNavigation) LAlert(title = tr("app.projects.slug.screenshots.design_screen.save_or_cancel_this_caption_before", "Save or cancel this caption before leaving Canvas or changing the selection."))
        ScreenshotsOfflineState()
        state.refreshError?.let { AsyncFailureState(it, subject = t("app.projects.slug.screenshots.design_screen.subject", "the screenshot plan"), onRetry = { reload++ }) }
        when (val snapshot = state.state) {
            Async.Loading -> LoadingState(rows = 5)
            is Async.Failure -> AsyncFailureState(snapshot, subject = t("app.projects.slug.screenshots.design_screen.subject", "the screenshot plan"), onRetry = { reload++ })
            is Async.Success -> key(subject, params.platform) {
                val data = snapshot.value
                LaunchedEffect(data.plan.slots.isEmpty()) {
                    if (data.plan.slots.isEmpty()) ctx.router.tryRoutingTo(screenshotsPath(slug, ScreenshotsTab.PLAN, params))
                }
                var pushLocked by remember(data) { mutableStateOf(data.pushLocked) }
                LaunchedEffect(data, subject) {
                    val current = subject ?: return@LaunchedEffect
                    while (true) {
                        delay(10_000)
                        if (document.asDynamic().hidden != true) {
                            pushLocked = try {
                                requestProjectResource(current.action, data.project.slug) {
                                    ApiClient.screenshotPushJobs(data.project.slug, data.plan.platform).any {
                                        it.state in setOf(ShotPushState.QUEUED, ShotPushState.RUNNING, ShotPushState.WAITING_FOR_VERSION)
                                    }
                                }
                            } catch (cancelled: CancellationException) { throw cancelled }
                            catch (failure: ApiException) { if (failure.status == 404) false else null }
                            catch (_: Throwable) { null }
                        }
                    }
                }
                if (!authority.canWriteTranslations) ScreenshotsReadOnlyState()
                if (pushLocked == true) ScreenshotsPushRunningState()
                if (pushLocked == null) LAlert(title = t("app.projects.slug.screenshots.design_screen.push_unknown", "The push status couldn't load. Editing is paused until it can be checked."))
                data.warnings.forEach { LAlert(title = it, variant = LAlertVariant.WARNING) }
                if (data.warnings.isNotEmpty()) LButton(t("app.projects.slug.screenshots.design_screen.retry_data", "Retry missing data"), onClick = { reload++ }, variant = LButtonVariant.SECONDARY)
                if (data.plan.slots.isNotEmpty()) DesignScreen(data, params,
                    canWrite = authority.canWriteTranslations && pushLocked == false, templates = templates,
                    onParams = { params = it; UrlState.replace(it.toParams(if (templates) null else ScreenshotsTab.DESIGN) +
                        ("mode" to mode.id.takeIf { !templates && mode == DesignMode.CANVAS })) },
                    mode = mode, onMode = { selected ->
                        mode = selected
                        UrlState.replace(params.toParams(ScreenshotsTab.DESIGN) + ("mode" to selected.id.takeIf { selected == DesignMode.CANVAS }))
                    }, navigate = { ctx.router.tryRoutingTo(it) }, onPendingCaption = { captionOpen = it }, onCaptionSaved = { reload++ })
            }
        }
    }
}

/** Parametric controls commit immutable documents; captions remain links to the translation editor. */
@Composable
internal fun DesignScreen(
    data: DesignPageData, params: ScreenshotsAreaState, canWrite: Boolean, templates: Boolean,
    onParams: (ScreenshotsAreaState) -> Unit, looks: DesignLooksPort = DesignLooks.current,
    mode: DesignMode = DesignMode.LAYOUT, onMode: (DesignMode) -> Unit = {},
    navigate: (String) -> Unit = { window.location.assign(it) },
    onPendingCaption: (Boolean) -> Unit = {}, onCaptionSaved: () -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    val runner = remember(data.project.id, canWrite) { ScreenshotsActionRunner(data.project.slug, { scope.launch { it() } }) }
    val controller = rememberDesignAutosave(data.project.slug, data.plan.platform, data.design, data.saved?.version ?: 0, canWrite,
        save = { request, success, failure -> runner.run(ScreenshotsPrivilege.DESIGN, {},
            { ApiClient.putScreenshotDesign(data.project.slug, data.plan.platform, request) }, failure, success) },
        loadLatest = { success, failure -> runner.run(ScreenshotsPrivilege.DESIGN, {},
            { ApiClient.screenshotDesign(data.project.slug, data.plan.platform) }, failure, success) })
    val model = remember(data.plan, looks) { DesignModel(data.plan, looks) }
    var error by remember { mutableStateOf<String?>(null) }
    val devices = (data.plan.slots.flatMap { it.devices } + controller.document?.boards.orEmpty().mapNotNull { DeviceClass.fromId(it.deviceClass) }).distinct()
    val device = params.device?.takeIf { it in devices } ?: devices.first()
    val locales = (listOf(data.project.sourceLocale) + data.project.locales).distinct()
    val locale = params.locale?.takeIf { it in locales } ?: data.project.sourceLocale
    val narrow = rememberDesignNarrow()
    var restoring by remember { mutableStateOf(false) }
    var pendingCaption by remember { mutableStateOf(false) }
    var shownMode by remember { mutableStateOf(mode) }
    LaunchedEffect(mode, pendingCaption) {
        if (!pendingCaption) shownMode = mode
        else if (mode != shownMode) onMode(shownMode)
    }
    SideEffect { onPendingCaption(pendingCaption) }
    val doc = controller.document
    var artwork by remember(data.project.id, data.artwork) { mutableStateOf(data.artwork) }
    val edit: ((ScreenshotDesign) -> ScreenshotDesign) -> Unit = { change ->
        val current = controller.document
        if (canWrite && !restoring && current != null) try { controller.edit(change(current)); error = null }
        catch (failure: IllegalArgumentException) { error = failure.message }
    }
    var uploadBusy by remember { mutableStateOf(false) }
    var progress by remember { mutableStateOf<BackgroundUploadProgress?>(null) }
    val uploader = remember { XhrUpload() }
    DisposableEffect(uploader) { onDispose { uploader.abort() } }
    if (!looks.available) LAlert(title = t("app.projects.slug.screenshots.design_screen.catalog_unavailable", "Layouts and templates are unavailable in this build. Existing designs can still be reviewed."))
    error?.let { LAlert(title = it, variant = LAlertVariant.WARNING) }
    Row(Modifier.fillMaxWidth().gap(Space.MD).styleModifier { property("flex-wrap", "wrap"); property("align-items", "end") }) {
        if (!templates) LSegmented(listOf(
            LSegmentedOption(DesignMode.LAYOUT, t("app.projects.slug.screenshots.design_screen.layout", "Layout")),
            LSegmentedOption(DesignMode.CANVAS, t("app.projects.slug.screenshots.design_screen.canvas", "Canvas")),
        ), shownMode, { selected ->
            if (pendingCaption) error = tr("app.projects.slug.screenshots.design_screen.save_or_cancel_this_caption_before", "Save or cancel this caption before leaving Canvas or changing the selection.")
            else { controller.flush(); shownMode = selected; onMode(selected) }
        }, label = t("app.projects.slug.screenshots.design_screen.design_mode", "Design mode"))
        LSelect(device.id, devices.map { LSelectOption(it.id, screenshotDeviceLabel(it)) }, { raw ->
            if (pendingCaption) return@LSelect
            controller.flush()
            devices.firstOrNull { it.id == raw }?.let { onParams(params.copy(device = it)) }
        }, label = t("app.projects.slug.screenshots.design_screen.device", "Device"))
        DesignResize(data, controller, device, locale, looks, runner,
            canWrite && !pendingCaption && data.pushLocked == false, onOpen = { restoring = it }, canOpen = !uploadBusy && !restoring)
        LMuted(controller.statusLabel)
        if (controller.state == DesignSaveState.FAILED) LButton(t("app.projects.slug.screenshots.design_screen.retry", "Retry"), onClick = controller::retry, variant = LButtonVariant.SECONDARY)
        LLinkButton(t("app.projects.slug.screenshots.design_screen.review", "Review set"), screenshotsPath(data.project.slug, ScreenshotsTab.REVIEW, params))
    }
    if (device == DeviceClass.PLAY_FEATURE_GRAPHIC) FeatureGraphicNotice()
    controller.draftTime?.let { time ->
        LAlert(title = trf("app.projects.slug.screenshots.design_screen.draft", "You have unsaved changes from {time}.", "time" to time))
        Row(Modifier.gap(Space.SM)) {
            if (canWrite) LButton(t("app.projects.slug.screenshots.design_screen.restore", "Restore them"), onClick = controller::restoreDraft, variant = LButtonVariant.SECONDARY)
            LButton(t("app.projects.slug.screenshots.design_screen.discard", "Discard draft"), onClick = controller::discardDraft, variant = LButtonVariant.GHOST)
        }
    }
    controller.issues.forEach { LAlert(title = it, variant = LAlertVariant.WARNING) }
    if (!pendingCaption) DesignConflict(controller)
    DesignVersions(data, controller, runner, looks, device, locale, canWrite && !pendingCaption, onRestoring = { restoring = it })
    SavedTemplateLibrary(data, controller, runner, canWrite && !restoring && !pendingCaption, templates) { restoring = it }
    if (!templates) WorkspaceAssetLibrary(data, runner, canWrite && !restoring && !pendingCaption) { asset ->
        artwork = (artwork.filterNot { it.id == asset.id } + asset)
        when (asset.kind) {
            DesignAssetKind.BACKGROUND -> edit { model.backgroundImage(it, device, asset.id) }
            DesignAssetKind.LOGO -> edit { it.copy(brand = it.brand.copy(logoAssetId = asset.id)) }
            DesignAssetKind.DEVICE_ART -> Unit
        }
    }
    if (templates) {
        TemplatesGallery(data, doc, device, locale, looks, canWrite && !restoring && !narrow, params, onParams,
            onUse = { selected ->
                try {
                    val changed = if (doc == null) model.build(selected, BrandKit(data.project.name.take(60))) else model.switchLayout(doc, selected)
                    if (controller.applyTemplate(changed)) changed else null
                } catch (failure: IllegalArgumentException) { error = failure.message; null }
            }, saveState = controller.state, saveVersion = controller.version)
        return
    }
    Div(DesignNarrowNoticeStyle.toModifier().toAttrs()) { LAlert(title = t("app.projects.slug.screenshots.design_screen.wider", "Shared layout and canvas editing need a wider screen. You can review languages here.")) }
    if (doc == null) {
        LAlert(title = if (canWrite) t("app.projects.slug.screenshots.design_screen.no_design_yet_choose_a_layout", "No design yet. Choose a layout to start designing your screenshots.")
            else t("app.projects.slug.screenshots.design_screen.no_design_yet_a_project_editor", "No design yet. A project editor can choose a layout; you can browse templates and review captures."))
        if (!narrow && looks.available) LayoutPicker(looks.layouts(), null, canWrite, onSelect = { layout ->
            try { controller.edit(model.build(layout, BrandKit(data.project.name.take(60)))) } catch (failure: IllegalArgumentException) { error = failure.message }
        }, preview = { layout -> LayoutSample(data, layout, device, locale, looks) })
        LLinkButton(t("app.projects.slug.screenshots.design_screen.gallery", "Browse templates"), screenshotsTemplatesPath(data.project.slug, params), variant = LButtonVariant.SECONDARY)
        return
    }
    val board = doc.boards.firstOrNull { it.deviceClass == device.id }
    if (board == null) {
        LMuted(t("app.projects.slug.screenshots.design_screen.new_device", "This device has no layout yet. Copy one at this device's store size."))
        if (canWrite && !narrow && looks.available) doc.boards.forEach { source ->
            val from = DeviceClass.fromId(source.deviceClass) ?: return@forEach
            LButton(trf("app.projects.slug.screenshots.design_screen.copy", "Copy layout from {device}", "device" to from.displayName), variant = LButtonVariant.SECONDARY, onClick = { edit { model.copyLayout(it, from, device) } })
        }
        return
    }
    val brandPanel: @Composable (ScreenshotDesign) -> Unit = { current ->
        BrandPanel(current.brand, looks, canWrite && !restoring && looks.available, data.project.hasCustomIcon,
            onColors = { primary, secondary -> edit { model.brand(it, primary, secondary) } },
            onFonts = { heading, body -> edit { model.fonts(it, heading, body) } }, onIcon = {
                runner.run(ScreenshotsPrivilege.DESIGN, {}, { projectIconPixels(data.project.slug) },
                { error = tr("app.projects.slug.screenshots.design_screen.icon_failed", "Couldn't read the project icon. Try again.") }, { pixels ->
                    val colors = looks.iconPalette(pixels, 64, 64).take(5)
                    edit { model.brand(it, colors.firstOrNull(), colors.getOrNull(1), colors) }
                })
            })
    }
    if (shownMode == DesignMode.CANVAS) {
        var openedOnDesktop by remember { mutableStateOf(!narrow) }
        SideEffect { if (!narrow) openedOnDesktop = true }
        if (openedOnDesktop) CanvasDesignWorkspace(data.copy(artwork = artwork), controller, params, looks, canWrite && !restoring, narrow,
            onParams, navigate, { pendingCaption = it }, onCaptionSaved) { current ->
            brandPanel(current)
        }
        if (narrow) {
            DesignLocalePanel(data, doc, device, locale, params, canWrite && !restoring && !pendingCaption, onParams, edit, canNavigate = !pendingCaption)
            LSelect(locale, locales.map { LSelectOption(it, localeInfo(it).englishName) }, { onParams(params.copy(locale = it)) },
                label = t("app.projects.slug.screenshots.design_screen.language", "Language"), enabled = !pendingCaption)
            ShotPreviewGrid(data, doc, device, locale, looks, onFits = {})
        }
        DesignCanvasReview(data, controller, params, device, locale, canWrite && !restoring, onParams)
        return
    }
    DesignLocalePanel(data, doc, device, locale, params, canWrite && !restoring, onParams, edit)
    Div(DesignWorkspaceBodyStyle.toModifier().toAttrs()) {
        Column(DesignEditingControlsStyle.toModifier().fillMaxWidth().gap(Space.MD)) {
            if (looks.available) LayoutPicker(looks.layouts(), doc.templateId, canWrite && !restoring, onSelect = { selected -> edit { model.switchLayout(it, selected) } },
                preview = { layout -> LayoutSample(data, layout, device, locale, looks) })
            FramePicker(device, board.pages.flatMap { layers(it.layers) }.filterIsInstance<DeviceLayer>().firstOrNull()?.frame ?: FrameKind.NONE, looks, canWrite && !restoring) { frame -> edit { model.frame(it, device, frame) } }
            brandPanel(doc)
            BackgroundPicker(canWrite && !restoring, board.pages.any { it.layers.any { layer -> isDesignBackgroundImage(layer) } },
                board.pages.any { it.layers.any { layer -> isDesignBackgroundScrim(layer) } }, uploadBusy, progress,
                onFill = { fill -> edit { model.background(it, device, fill) } }, onScrim = { enabled -> edit { model.scrim(it, device, enabled) } }, onUpload = { file ->
                    val refusal = backgroundRefusal(file)
                    if (refusal != null) error = refusal else if (!uploadBusy) {
                        val assetId = "background-${Date.now().toLong()}-${kotlin.random.Random.nextInt(1_000_000)}"
                        runner.run(ScreenshotsPrivilege.DESIGN, { uploadBusy = it },
                            { ApiClient.putScreenshotDesignAsset(data.project.slug, doc.platform, assetId, DesignAssetKind.BACKGROUND, file, file.type,
                                uploader, { done, total -> progress = BackgroundUploadProgress(done, total) }) },
                            { failure -> error = (failure as? ApiException)?.message ?: tr("app.projects.slug.screenshots.design_screen.upload_failed", "Couldn't upload the background. Try again."); progress = null },
                            { asset -> edit { model.backgroundImage(it, device, asset.id) }; progress = null })
                    }
                })
            board.pages.flatMap { layers(it.layers) }.filterIsInstance<TextLayer>().firstOrNull { it.content is TextContent.SlotCaption }?.let { caption ->
                CaptionSizes(caption.fit, canWrite && !restoring) { max, floor -> edit { model.captionSizes(it, device, max, floor) } }
            }
            data.plan.slots.filter { device in it.devices }.forEach { slot ->
                if (canWrite && !restoring) LColorField(doc.palette.slotAccents.getOrNull(data.plan.slots.sortedBy { it.position }.indexOf(slot)) ?: doc.palette.accent,
                    { color -> edit { model.slotAccent(it, slot.key, color) } }, label = trf("app.projects.slug.screenshots.design_screen.accent", "Accent for {shot}", "shot" to slot.title))
            }
            LLinkButton(t("app.projects.slug.screenshots.design_screen.templates_link", "Templates"), screenshotsTemplatesPath(data.project.slug, params), variant = LButtonVariant.SECONDARY)
        }
    var longestFirst by remember { mutableStateOf(false) }
    val orderedLocales = locales
    Div(attrs = { onKeyDown { event ->
        val tag = event.target.asDynamic().tagName as? String
        if (tag !in setOf("INPUT", "TEXTAREA", "SELECT") && event.key in setOf("ArrowLeft", "ArrowRight")) {
            event.preventDefault()
            val next = (orderedLocales.indexOf(locale) + if (event.key == "ArrowRight") 1 else -1 + orderedLocales.size) % orderedLocales.size
            onParams(params.copy(locale = orderedLocales[(next + orderedLocales.size) % orderedLocales.size]))
        }
    } }) {
        Row(Modifier.gap(Space.SM).styleModifier { property("flex-wrap", "wrap") }) {
            LSelect(locale, orderedLocales.map { LSelectOption(it, localeInfo(it).englishName) }, { onParams(params.copy(locale = it)) }, label = t("app.projects.slug.screenshots.design_screen.language", "Language"))
            LButton("←", onClick = { onParams(params.copy(locale = orderedLocales[(orderedLocales.indexOf(locale) - 1 + orderedLocales.size) % orderedLocales.size])) }, variant = LButtonVariant.SECONDARY,
                accessibleLabel = t("app.projects.slug.screenshots.design_screen.previous", "← Previous language"))
            LButton("→", onClick = { onParams(params.copy(locale = orderedLocales[(orderedLocales.indexOf(locale) + 1) % orderedLocales.size])) }, variant = LButtonVariant.SECONDARY,
                accessibleLabel = t("app.projects.slug.screenshots.design_screen.next", "→ Next language"))
            LSelect(if (longestFirst) "longest" else "project", listOf(LSelectOption("project", t("app.projects.slug.screenshots.design_screen.project_order", "Project order")),
                LSelectOption("longest", t("app.projects.slug.screenshots.design_screen.longest", "Longest caption first"))), { longestFirst = it == "longest" }, label = t("app.projects.slug.screenshots.design_screen.sort", "Shot order"))
        }
        var fits by remember(doc, device) { mutableStateOf<List<CaptionFit>>(emptyList()) }
        ShotPreviewGrid(data, doc, device, locale, looks, onFits = { fits = it }, longestFirst = longestFirst,
            focusSlot = data.plan.slots.firstOrNull { it.id == params.slot }?.key)
        FitSummary(fits, data, board.pages.sumOf { it.slotIds.size } * locales.size)
    }
    }
}
